import React, { useEffect, useState } from 'react';
import { tokenApi } from '../services/api';

interface TokenBalance {
  total_recharged: number;
  used: number;
  remaining: number;
  usage_percent: number;
  is_overdraft: boolean;
  need_warning: boolean;
}

/**
 * Token使用进度条组件
 * 显示在页面顶部，展示Token使用情况
 */
const TokenProgressBar: React.FC = () => {
  const [balance, setBalance] = useState<TokenBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [showTooltip, setShowTooltip] = useState(false);

  // 加载Token余额
  const loadBalance = async () => {
    try {
      const response = await tokenApi.getBalance();
      setBalance(response.data);
    } catch (error) {
      console.error('加载Token余额失败:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // 初始加载一次
    loadBalance();
    
    // 监听AI调用完成事件，实时更新Token余额
    const handleTokenUsed = () => {
      console.log('🔄 检测到Token使用，刷新进度条');
      loadBalance();
    };
    
    window.addEventListener('token:used', handleTokenUsed);
    
    return () => {
      window.removeEventListener('token:used', handleTokenUsed);
    };
  }, []);

  if (loading || !balance) {
    return null; // 加载中不显示
  }

  // 计算进度条颜色（根据剩余比例）
  const getProgressColor = () => {
    // 剩余百分比
    const remainingPercent = 100 - balance.usage_percent;
    
    if (balance.is_overdraft) {
      return 'bg-red-500'; // 欠费：红色
    }
    if (balance.need_warning || remainingPercent < 20) {
      return 'bg-yellow-500'; // 预警或剩余不足20%：黄色
    }
    if (remainingPercent < 30) {
      return 'bg-orange-500'; // 剩余不足30%：橙色
    }
    return 'bg-green-500'; // 剩余充足：绿色
  };

  // 计算进度条宽度（显示剩余百分比，确保在0-100之间）
  const remainingPercent = 100 - balance.usage_percent;
  const progressWidth = Math.min(Math.max(remainingPercent, 0), 100);

  // 格式化数字
  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toLocaleString();
  };

  return (
    <div 
      className="w-full absolute top-0 left-0 right-0"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
    >
      {/* 进度条容器 - 绝对定位，不占空间 */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gray-100">
        {/* 进度条 */}
        <div
          className={`absolute top-0 left-0 h-full transition-all duration-500 ${getProgressColor()}`}
          style={{ width: `${progressWidth}%` }}
        />
      </div>

      {/* 悬浮时显示详细信息 */}
      {showTooltip && (
        <div className="absolute top-1 left-0 right-0 z-50 bg-white border border-gray-200 shadow-lg rounded-b-md p-3 animate-fadeIn">
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center space-x-4">
              <div>
                <span className="text-gray-500">已使用：</span>
                <span className="font-semibold text-gray-900">
                  {formatNumber(balance.used)} / {formatNumber(balance.total_recharged)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">剩余：</span>
                <span className={`font-semibold ${balance.remaining < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatNumber(balance.remaining)}
                </span>
              </div>
              <div>
                <span className="text-gray-500">使用率：</span>
                <span className="font-semibold text-gray-900">
                  {balance.usage_percent.toFixed(1)}%
                </span>
              </div>
            </div>
            
            {/* 状态标签 */}
            {balance.is_overdraft && (
              <span className="px-2 py-1 text-xs font-medium text-red-700 bg-red-100 rounded">
                欠费状态
              </span>
            )}
            {!balance.is_overdraft && balance.need_warning && (
              <span className="px-2 py-1 text-xs font-medium text-yellow-700 bg-yellow-100 rounded">
                余额不足
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenProgressBar;
